import React from "react";
import {
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography,
} from "@material-ui/core";
import CheckCircleOutline from "@material-ui/icons/CheckCircleOutline";
import ErrorOutline from "@material-ui/icons/ErrorOutline";
import { t } from "adapters/i18n";
import type {
  EditableCyclePeriod,
  ForageCycleRecord,
} from "../../../forageCycleManagementUtils";

interface CycleDraftSummary {
  cycle: ForageCycleRecord;
  periods: EditableCyclePeriod[];
}

interface SummaryPeriodRow {
  key: string;
  label: string;
  period: string;
  heights: string;
  rest: string;
}

interface SummaryForage {
  key: string;
  name: string;
  periods: SummaryPeriodRow[];
}

interface ForageCycleDialogsProps {
  confirmationOpen: boolean;
  drafts: CycleDraftSummary[];
  errorDialogOpen: boolean;
  hasFailedRequests: boolean;
  saveError: string;
  saving: boolean;
  onCloseConfirmation: () => void;
  onCloseError: () => void;
  onConfirmSave: () => void;
  onRetrySave: () => void;
}

function getSummaryForages(drafts: CycleDraftSummary[]): SummaryForage[] {
  return drafts.map(({ cycle, periods }) => {
    const forageName =
      cycle.nomeForragem ||
      t("forageCycle:cycle-forage-id", { id: cycle.IdForragem }) ||
      `Forragem ${cycle.IdForragem}`;
    const forageKey = `${cycle.IdFazenda}-${cycle.IdForragem}`;

    return {
      key: forageKey,
      name: forageName,
      periods: periods.map((period, index) => {
        const unit = t("forageCycle:cycle-unit-cm");
        return {
          key: String(
            period.IdPeriodoCicloForragemFazenda ||
              `novo-${forageKey}-${index}`
          ),
          label:
            period.Rotulo ||
            t("forageCycle:cycle-period-title", { number: index + 1 }),
          period: `${period.dataInicio} - ${period.dataFim}`,
          heights: `${period.AlturaEntrada} ${unit} - ${period.AlturaSaida} ${unit}`,
          rest: `${period.QuantidadeDiasDescanso}${t("forageCycle:cycle-unit-days-short")}`,
        };
      }),
    };
  });
}

function ForageCycleDialogs({
  confirmationOpen,
  drafts,
  errorDialogOpen,
  hasFailedRequests,
  saveError,
  saving,
  onCloseConfirmation,
  onCloseError,
  onConfirmSave,
  onRetrySave,
}: ForageCycleDialogsProps) {
  const summaryForages = getSummaryForages(drafts);

  return (
    <>
      <Dialog
        open={confirmationOpen}
        onClose={() => !saving && onCloseConfirmation()}
        fullWidth
        maxWidth="sm"
        PaperProps={{ className: "forage-cycle-dialog" }}
        aria-labelledby="cycle-confirm-title"
        aria-describedby="cycle-confirm-description"
      >
        <DialogTitle
          disableTypography
          className="forage-cycle-dialog__title"
        >
          <div className="forage-cycle-dialog__icon forage-cycle-dialog__icon--success">
            <CheckCircleOutline />
          </div>
          <Typography
            id="cycle-confirm-title"
            component="h2"
            className="forage-cycle-dialog__heading"
          >
            {t("forageCycle:cycle-confirm-title")}
          </Typography>
        </DialogTitle>
        <DialogContent>
          <Typography
            id="cycle-confirm-description"
            className="forage-cycle-dialog__description"
          >
            {t("forageCycle:cycle-confirm-description")}
          </Typography>
          <div
            className="forage-cycle-confirmation__summary"
            data-testid="cycle-confirm-summary"
          >
            {summaryForages.map((forage, index) => (
              <section
                aria-labelledby={`cycle-summary-forage-title-${index}`}
                className="forage-cycle-confirmation__forage"
                data-testid={`cycle-summary-forage-${forage.key}`}
                key={forage.key}
              >
                <Typography
                  component="h3"
                  className="forage-cycle-confirmation__forage-title"
                  id={`cycle-summary-forage-title-${index}`}
                >
                  {forage.name}
                </Typography>
                <table
                  aria-label={forage.name}
                  className="forage-cycle-confirmation__table"
                >
                  <thead>
                    <tr>
                      <th scope="col">{t("forageCycle:cycle-period-label")}</th>
                      <th scope="col">
                        {t("forageCycle:cycle-summary-column-period")}
                      </th>
                      <th scope="col">
                        {t("forageCycle:cycle-summary-column-heights")}
                      </th>
                      <th scope="col">{t("forageCycle:cycle-rest-days")}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {forage.periods.length ? (
                      forage.periods.map((period) => (
                        <tr key={period.key}>
                          <td>{period.label}</td>
                          <td>{period.period}</td>
                          <td>{period.heights}</td>
                          <td>{period.rest}</td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td
                          className="forage-cycle-confirmation__empty"
                          colSpan={4}
                        >
                          {t("forageCycle:cycle-summary-empty")}
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </section>
            ))}
          </div>
        </DialogContent>
        <DialogActions className="forage-cycle-dialog__actions">
          <Button
            disabled={saving}
            onClick={onCloseConfirmation}
            className="forage-cycle-dialog__secondary-action"
          >
            {t("common:actions.cancel")}
          </Button>
          <Button
            color="primary"
            variant="contained"
            disabled={saving}
            onClick={onConfirmSave}
            className="forage-cycle-dialog__primary-action"
          >
            {saving ? (
              <CircularProgress size={16} />
            ) : (
              t("forageCycle:cycle-confirm-save")
            )}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={errorDialogOpen}
        onClose={onCloseError}
        fullWidth
        maxWidth="xs"
        PaperProps={{ className: "forage-cycle-dialog" }}
        aria-labelledby="cycle-save-error-title"
        aria-describedby="cycle-save-error-description"
      >
        <DialogTitle
          disableTypography
          className="forage-cycle-dialog__title"
        >
          <div className="forage-cycle-dialog__icon forage-cycle-dialog__icon--error">
            <ErrorOutline />
          </div>
          <Typography
            id="cycle-save-error-title"
            component="h2"
            className="forage-cycle-dialog__heading"
          >
            {t("forageCycle:cycle-save-error-title")}
          </Typography>
        </DialogTitle>
        <DialogContent>
          <Typography
            id="cycle-save-error-description"
            role="alert"
            className="forage-cycle-dialog__description forage-cycle-dialog__description--centered"
          >
            {saveError || t("forageCycle:cycle-save-error")}
          </Typography>
        </DialogContent>
        <DialogActions className="forage-cycle-dialog__actions forage-cycle-dialog__actions--centered">
          <Button
            onClick={onCloseError}
            className="forage-cycle-dialog__secondary-action"
          >
            {t("forageCycle:cycle-close-error")}
          </Button>
          <Button
            color="primary"
            variant="contained"
            disabled={saving || !hasFailedRequests}
            onClick={onRetrySave}
            className="forage-cycle-dialog__primary-action"
          >
            {saving ? (
              <CircularProgress size={16} />
            ) : (
              t("forageCycle:cycle-retry-save")
            )}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}

export default ForageCycleDialogs;
